import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  Mail,
  BookOpen,
  Trash2,
  RefreshCw,
  Download,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "@/lib/auth";
import { AdminTopbar } from "@/components/admin/AdminTopbar";
import { AdminTabs } from "@/components/admin/AdminTabs";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-SG", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/leads?limit=500");
      setItems(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not load downloads."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      [it.name, it.email, it.property_type]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, query]);

  const stats = useMemo(() => {
    const total = items.length;
    const last7 = items.filter((i) => {
      const t = new Date(i.created_at).getTime();
      return t > Date.now() - 7 * 24 * 3600 * 1000;
    }).length;
    const last30 = items.filter((i) => {
      const t = new Date(i.created_at).getTime();
      return t > Date.now() - 30 * 24 * 3600 * 1000;
    }).length;
    const uniqueEmails = new Set(items.map((i) => i.email?.toLowerCase())).size;
    return { total, last7, last30, uniqueEmails };
  }, [items]);

  const remove = async (id) => {
    if (!window.confirm("Remove this download record?")) return;
    try {
      await apiClient.delete(`/leads/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Removed.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Delete failed."
      );
    }
  };

  const exportCSV = () => {
    if (!items.length) {
      toast.error("Nothing to export yet.");
      return;
    }
    const header = ["name", "email", "property_type", "source", "created_at"];
    const rows = items.map((it) =>
      header
        .map((k) => {
          const v = it[k] == null ? "" : String(it[k]);
          return `"${v.replace(/"/g, '""')}"`;
        })
        .join(",")
    );
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `noah-rayn-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded.");
  };

  return (
    <div className="min-h-screen bg-[color:var(--nr-bg)]">
      <Toaster position="top-center" richColors />
      <AdminTopbar />

      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
        <AdminTabs />

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div>
            <p className="overline mb-3">Top-of-funnel</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-[color:var(--nr-ink)] tracking-tight">
              Guide Downloads
            </h1>
            <p className="text-sm text-[color:var(--nr-ink-soft)] mt-2 max-w-xl">
              Visitors who downloaded the 2026 BTO Renovation Cost Guide. Reach
              out within 48h while the home is still on their mind.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-5">
            {[
              { k: "Total", v: stats.total },
              { k: "Last 7d", v: stats.last7 },
              { k: "Last 30d", v: stats.last30 },
              { k: "Unique", v: stats.uniqueEmails },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`admin-lead-stat-${s.k.toLowerCase().replace(/\s/g, "-")}`}
                className="rounded-2xl bg-[color:var(--nr-bg-soft)] border border-[color:var(--nr-line)] px-5 py-4 min-w-[100px]"
              >
                <div className="text-[0.65rem] uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">
                  {s.k}
                </div>
                <div className="font-serif-display text-3xl text-[color:var(--nr-ink)] mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
          <div className="flex-1 relative">
            <Search
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--nr-ink-soft)]"
            />
            <input
              data-testid="admin-leads-search"
              type="search"
              placeholder="Search name, email, property…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-full bg-[color:var(--nr-bg-soft)] border border-[color:var(--nr-line)] text-sm focus:outline-none focus:border-[color:var(--nr-accent)] transition-colors"
            />
          </div>
          <button
            data-testid="admin-leads-refresh"
            onClick={load}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[color:var(--nr-line)] text-xs uppercase tracking-[0.18em] hover:bg-[color:var(--nr-bg-soft)]"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            data-testid="admin-leads-export"
            onClick={exportCSV}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[color:var(--nr-ink)] text-white text-xs uppercase tracking-[0.18em] hover:bg-black"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>

        <div className="bg-[color:var(--nr-bg-soft)] border border-[color:var(--nr-line)] rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-sm text-[color:var(--nr-ink-soft)]">
              Loading downloads…
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="admin-leads-empty"
              className="p-16 text-center"
            >
              <BookOpen
                size={36}
                className="mx-auto text-[color:var(--nr-accent)]/50 mb-4"
              />
              <p className="font-serif-display text-2xl text-[color:var(--nr-ink)]">
                {query ? "No matches." : "No downloads yet."}
              </p>
              <p className="text-sm text-[color:var(--nr-ink-soft)] mt-2">
                {query
                  ? "Try a different search term."
                  : "Once visitors download the guide, they'll appear here."}
              </p>
            </div>
          ) : (
            <table className="w-full text-left" data-testid="admin-leads-table">
              <thead className="bg-[color:var(--nr-bg-deep)]/50">
                <tr className="text-[0.65rem] uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">
                  <th className="px-6 py-4 font-semibold">Name</th>
                  <th className="px-6 py-4 font-semibold">Email</th>
                  <th className="px-6 py-4 font-semibold">Journey stage</th>
                  <th className="px-6 py-4 font-semibold">Downloaded</th>
                  <th className="px-6 py-4 font-semibold w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--nr-line)]">
                {filtered.map((it) => (
                  <tr
                    key={it.id}
                    data-testid={`admin-lead-row-${it.id}`}
                    className="hover:bg-[color:var(--nr-bg)]/60 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="font-medium text-[color:var(--nr-ink)]">
                        {it.name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <a
                        href={`mailto:${it.email}`}
                        className="text-sm text-[color:var(--nr-ink)] hover:text-[color:var(--nr-accent)] inline-flex items-center gap-1.5"
                      >
                        <Mail size={12} />
                        {it.email}
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      {it.property_type ? (
                        <span className="text-[0.7rem] px-2 py-1 rounded-full bg-[color:var(--nr-accent)]/10 text-[color:var(--nr-accent)]">
                          {it.property_type}
                        </span>
                      ) : (
                        <span className="text-xs text-[color:var(--nr-ink-soft)]">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-[color:var(--nr-ink-soft)]">
                      {fmtDate(it.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => remove(it.id)}
                        data-testid={`admin-lead-delete-${it.id}`}
                        aria-label="Delete download record"
                        className="p-2 rounded-full text-[#A33A1F] hover:bg-[#FBE9E0]"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className="mt-6 text-xs text-[color:var(--nr-ink-soft)]">
          Tip: visitors who download the guide are 2–3× more likely to book a
          consultation within 30 days. A short personal WhatsApp message within
          48 hours converts best.
        </p>
      </main>
    </div>
  );
}
